import { FaArrowRight } from "react-icons/fa";
import { Link } from "react-router-dom";
import { displayDate } from "src/utils/dateHandler.util";
import { TaskFailure } from "../types/task-failure.entity";

type Props = {
  data: TaskFailure[];
  isDarkMode: boolean;
};

const getRelatedEntity = (taskFailure: TaskFailure) => {
  if (taskFailure.userId) {
    return {
      label: "User",
      value: taskFailure.userId,
      to: `/users/${taskFailure.userId}`,
    };
  }

  const [type, value] = Object.entries(taskFailure.relatedEntities)[0] ?? [];
  return value ? { label: type, value } : undefined;
};

export const TaskFailuresTable = ({ data, isDarkMode }: Props) => (
  <div className={`overflow-x-auto border ${isDarkMode ? "border-slate-700" : "border-slate-200"}`}>
    <table className="min-w-[1060px] w-full divide-y divide-slate-200 text-left text-sm dark:divide-slate-700">
      <thead className={isDarkMode ? "bg-slate-800 text-slate-200" : "bg-slate-900 text-white"}>
        <tr className="text-xs uppercase tracking-wide">
          <th className="px-4 py-3 font-semibold">Task</th>
          <th className="px-4 py-3 font-semibold">Related record</th>
          <th className="px-4 py-3 font-semibold">Last error</th>
          <th className="px-4 py-3 font-semibold text-center">Attempts</th>
          <th className="px-4 py-3 font-semibold">Last failed</th>
          <th className="px-4 py-3 font-semibold text-right">Open</th>
        </tr>
      </thead>
      <tbody className={isDarkMode ? "divide-y divide-slate-800" : "divide-y divide-slate-100"}>
        {data.map((taskFailure) => {
          const relatedEntity = getRelatedEntity(taskFailure);

          return (
            <tr
              key={taskFailure.id}
              className={isDarkMode ? "bg-slate-900/50 hover:bg-slate-800/80" : "bg-white hover:bg-slate-50"}
            >
              <td className="px-4 py-4 align-top">
                <span className={isDarkMode ? "block font-semibold text-slate-100" : "block font-semibold text-slate-900"}>
                  {taskFailure.functionName}
                </span>
                <span className={`mt-1 inline-flex border px-2 py-0.5 font-mono text-[11px] ${isDarkMode ? "border-slate-600 bg-slate-800 text-slate-300" : "border-slate-200 bg-slate-50 text-slate-600"}`}>
                  {taskFailure.queueName}
                </span>
              </td>
              <td className="px-4 py-4 align-top">
                {relatedEntity?.to ? (
                  <Link
                    to={relatedEntity.to}
                    className={`block max-w-[190px] no-underline ${isDarkMode ? "text-sky-300 hover:text-sky-200" : "text-sky-700 hover:text-sky-900"}`}
                  >
                    <span className="block text-xs">{relatedEntity.label}</span>
                    <span className="block truncate font-mono text-[11px]">{relatedEntity.value}</span>
                  </Link>
                ) : relatedEntity ? (
                  <div className={isDarkMode ? "text-slate-200" : "text-slate-700"}>
                    <span className="block text-xs">{relatedEntity.label}</span>
                    <span className="block max-w-[190px] truncate font-mono text-[11px]">{relatedEntity.value}</span>
                  </div>
                ) : (
                  <span className={isDarkMode ? "text-slate-500" : "text-slate-400"}>-</span>
                )}
              </td>
              <td className="px-4 py-4 align-top">
                <p className={`max-w-[340px] truncate ${isDarkMode ? "text-slate-200" : "text-slate-700"}`} title={taskFailure.lastError.message}>
                  {taskFailure.lastError.message}
                </p>
                {taskFailure.lastError.code && (
                  <span className={`mt-1 inline-flex border px-2 py-0.5 font-mono text-[11px] ${isDarkMode ? "border-rose-400/30 bg-rose-500/10 text-rose-200" : "border-rose-200 bg-rose-50 text-rose-700"}`}>
                    {taskFailure.lastError.code}
                  </span>
                )}
              </td>
              <td className="px-4 py-4 text-center align-top">
                <span className={`inline-flex min-w-[32px] justify-center border px-2 py-1 font-semibold ${isDarkMode ? "border-amber-400/30 bg-amber-500/10 text-amber-200" : "border-amber-200 bg-amber-50 text-amber-800"}`}>
                  {taskFailure.failureCount}
                </span>
              </td>
              <td className={`px-4 py-4 align-top ${isDarkMode ? "text-slate-200" : "text-slate-700"}`}>
                {displayDate(taskFailure.lastFailedAt)}
              </td>
              <td className="px-4 py-4 text-right align-top">
                <Link
                  to={`/task-failures/${taskFailure.id}`}
                  title="Open task failure"
                  aria-label="Open task failure"
                  className={`inline-flex h-9 w-9 items-center justify-center rounded border transition-colors ${isDarkMode ? "border-slate-600 text-slate-200 hover:border-sky-400 hover:bg-sky-500/15 hover:text-sky-200" : "border-slate-200 text-slate-600 hover:border-sky-300 hover:bg-sky-50 hover:text-sky-700"}`}
                >
                  <FaArrowRight className="text-sm" />
                </Link>
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  </div>
);
